import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  MenuItem,
  Select,
  CircularProgress,
  TextField,
  Button,
  Modal,
  TablePagination,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import toast from "react-hot-toast";
import DaterangePicker from "../../common/Daterangepicker";
import formatDateAndTime from "../../common/formatDateAndTime";
import { useSelector } from "react-redux";
import getactions from "../../common/getactions";

const FONT = "Outfit";

const StatCard = ({
  label,
  value,
  badge,
  badgeColor,
  badgeBg,
  sub,
  barColor,
}) => (
  <Box
    sx={{
      flex: 1,
      p: 3,
      position: "relative",
      bgcolor: "white",
      borderRadius: "16px",
      outline: "1px solid #E5E7EB",
      outlineOffset: "-1px",
      overflow: "hidden",
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={1.5}
    >
      <Typography
        sx={{
          color: "#6B7280",
          fontSize: 14,
          fontFamily: FONT,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ px: 1, py: 0.5, bgcolor: badgeBg, borderRadius: "6px" }}>
        <Typography
          sx={{
            color: badgeColor,
            fontSize: 11,
            fontFamily: FONT,
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {badge}
        </Typography>
      </Box>
    </Stack>
    <Typography
      sx={{
        color: "#111827",
        fontSize: 32,
        fontFamily: FONT,
        fontWeight: 700,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography
        sx={{ color: "#9CA3AF", fontSize: 12, fontFamily: FONT, mt: 0.5 }}
      >
        {sub}
      </Typography>
    )}
    <Box
      sx={{
        width: 84,
        height: 12,
        position: "absolute",
        right: 24,
        top: 0,
        bgcolor: barColor,
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
      }}
    />
  </Box>
);

const FilterPill = ({ label, value, options, onChange }) => (
  <Box
    sx={{
      flex: 1,
      px: 1.75,
      py: 1.25,
      bgcolor: "white",
      borderRadius: "8px",
      outline: "1px solid #E5E7EB",
      outlineOffset: "-1px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      position: "relative",
    }}
  >
    <Box>
      <Typography
        sx={{
          color: "#9CA3AF",
          fontSize: 11,
          fontFamily: FONT,
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#1F2937",
          fontSize: 14,
          fontFamily: FONT,
          fontWeight: 600,
          letterSpacing: 0.11,
          truncate: true,
          maxWidth: "160px",
        }}
      >
        {value}
      </Typography>
    </Box>
    <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#4B5563" }} />
    <Select
      value=""
      onChange={onChange}
      displayEmpty
      variant="standard"
      disableUnderline
      IconComponent={() => null}
      sx={{
        position: "absolute",
        inset: 0,
        opacity: 0,
        "& .MuiSelect-select": { height: "100%" },
      }}
    >
      <MenuItem value="">All</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.name}
        </MenuItem>
      ))}
    </Select>
  </Box>
);

const StatusBadge = ({ verified, rejected }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.75,
      bgcolor: verified ? "#ECFDF5" : rejected ? "#FEF2F2" : "#FFF7ED",
      borderRadius: "99px",
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      width: "fit-content",
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        bgcolor: verified ? "#059669" : rejected ? "#DC2626" : "#D97706",
        borderRadius: "9999px",
      }}
    />
    <Typography
      sx={{
        color: verified ? "#059669" : rejected ? "#DC2626" : "#D97706",
        fontSize: 13,
        fontFamily: FONT,
        fontWeight: 600,
      }}
    >
      {verified ? "Verified" : rejected ? "Rejected" : "Pending"}
    </Typography>
  </Box>
);

const Overtime = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const profile = JSON.parse(localStorage.getItem("profile"));
  const actionsToAccess = getactions(profile?.roles, profile?.actions);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statsRows, setStatsRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination states (fixed limit: 10)
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  // Dropdown options states
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const isSystemUserDataList = useSelector(
    (state) => state?.isSystemUser?.isSysTemUserData?.entities?.rows,
  );

  // Unsubmitted filter form states
  const [inputEmpId, setInputEmpId] = useState("");
  const [inputBookingId, setInputBookingId] = useState("");
  const [rejectionComment, setRejectionComment] = useState("");
  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [selectedVerifyItem, setSelectedVerifyItem] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const handleOpenVerifyModal = (item) => {
    setSelectedVerifyItem(item);
    setOpenVerifyModal(true);
  };

  const handleCloseVerifyModal = () => {
    setSelectedVerifyItem(null);
    setOpenVerifyModal(false);
    setRejectionComment("");
  };

  // Date range picker state
  const [selectionRange, setSelectionRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    key: "selection",
  });

  // Active query states
  const [appliedFilters, setAppliedFilters] = useState({
    empId: "",
    bookingCode: "",
    startDate: "",
    endDate: "",
  });

  // Fetch filter dropdown options on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const userRes = await fetch(
          `${apiUrl}/api/users?limit=1000&isSystemUser=1&isAgent=true`,
          {
            method: "GET",
            headers: { Authorization: `${token}` },
          },
        );
        const userData = await userRes.json();
        if (userData?.statusCode === "0100") {
          const formattedUsers = (userData?.entities?.rows || []).map((u) => ({
            id: String(u.id),
            name: u.name,
          }));
          setEmployeeOptions(formattedUsers);
        }
      } catch (err) {
        console.error("Error fetching filter options:", err);
      }
    };

    fetchDropdownData();
  }, [apiUrl, token]);

    const fetchOvertimeStats = async (filters) => {
    try {
      const params = new URLSearchParams();
      if (filters.empId) params.append("empId", filters.empId);
      if (filters.bookingCode)
        params.append("bookingCode", filters.bookingCode);
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);

      const queryString = params.toString().replace(/%3A/g, ":");

      const response = await fetch(
        `${apiUrl}/api/overtimedetails/fetch-details-with-search?${queryString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );

      const data = await response.json();
      if (data?.statusCode === "0100") {
        setStatsRows(data?.dataValues?.rows || []);
      } else {
        setStatsRows([]);
      }
    } catch (error) {
      console.error("Error fetching overtime stats:", error);
      setStatsRows([]);
    }
  };
  const fetchOvertimeData = async (filters, currentPage = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.empId) params.append("empId", filters.empId);
      if (filters.bookingCode)
        params.append("bookingCode", filters.bookingCode);
      if (filters.startDate) params.append("startDate", filters.startDate);
      if (filters.endDate) params.append("endDate", filters.endDate);

      params.append("limit", String(rowsPerPage));
      params.append("offset", String(currentPage * rowsPerPage));

      let queryString = params.toString().replace(/%3A/g, ":");

      const response = await fetch(
        `${apiUrl}/api/overtimedetails/fetch-details-with-search?${queryString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );

      const data = await response.json();
      if (data?.statusCode === "0100") {
        setRows(data?.dataValues?.rows || []);
        setTotalCount(data?.dataValues?.count || 0);
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching overtime data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchOvertimeData(appliedFilters, newPage);
  };

  const handleConfirmVerify = async (type) => {
    if (!selectedVerifyItem) return;
    let payload;
    if (type === "verify") {
      payload = {
        id: selectedVerifyItem.id,
        isVerified: true,
        bookingId: selectedVerifyItem.bookingId,
      };
    } else {
      payload = {
        id: selectedVerifyItem.id,
        isRejected: true,
        bookingId: selectedVerifyItem.bookingId,
        rejectionComment: rejectionComment,
      };
    }
    setVerifying(true);
    try {
      const response = await fetch(`${apiUrl}/api/overtimedetails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data?.statusCode === "0100" || response.ok) {
        handleCloseVerifyModal();
        setRejectionComment("");
        await fetchOvertimeData(appliedFilters, page);
      } else {
        console.error("Failed to verify overtime record:", data);
      }
    } catch (error) {
      console.error("Error during overtime verification API call:", error);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    fetchOvertimeData(appliedFilters, page);
    fetchOvertimeStats(appliedFilters);
  }, [appliedFilters]);

  const handleSearchClick = () => {
    const formatDate = (date, isEnd = false) => {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = isEnd ? "23" : String(date.getHours()).padStart(2, "0");
      const minutes = isEnd ? "59" : String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    setPage(0);
    const newFilters = {
      empId: inputEmpId,
      bookingCode: inputBookingId ? inputBookingId.trim() : "",
      startDate: formatDate(selectionRange.startDate, false),
      endDate: formatDate(selectionRange.endDate, true),
    };

    setAppliedFilters(newFilters);
  };

  const handleResetClick = () => {
    setInputEmpId("");
    setInputBookingId("");
    setSelectionRange({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
      key: "selection",
    });
    setPage(0);
    setAppliedFilters({
      empId: "",
      bookingCode: "",
      startDate: "",
      endDate: "",
    });
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (appliedFilters.empId) params.append("empId", appliedFilters.empId);
      if (appliedFilters.bookingCode)
        params.append("bookingCode", appliedFilters.bookingCode);
      if (appliedFilters.startDate)
        params.append("startDate", appliedFilters.startDate);
      if (appliedFilters.endDate)
        params.append("endDate", appliedFilters.endDate);

      const response = await fetch(
        `${apiUrl}/api/overtimedetails/download-report?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );

      const data = await response.json();
      if (data?.statusCode === "0100" && data?.dataValues?.Location) {
        const fileUrl = data.dataValues.Location;
        const link = document.createElement("a");
        link.href = fileUrl;
        link.setAttribute("download", "overtime_details.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("Report downloaded successfully!", {
          position: "top-center",
          style: {
            background: "white",
            color: "#111827",
            fontWeight: "550",
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
            border: "1px solid #E5E7EB",
          },
        });
      } else {
        toast.error("Failed to generate download report", {
          position: "top-center",
          style: { background: "white", color: "#111827" },
        });
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("An error occurred while exporting the report.", {
        position: "top-center",
        style: { background: "white", color: "#111827" },
      });
    }
  };

  const columns = [
    { key: "agent", label: "Agent", flex: 2, minWidth: 200, align: "left" },
    { key: "shiftStart", label: "Shift Start", flex: 1.2, minWidth: 150, align: "left" },
    { key: "shiftEnd", label: "Shift End", flex: 1.2, minWidth: 150, align: "left" },
    { key: "totalShift", label: "Total Shift", flex: 1, minWidth: 110, align: "left" },
    { key: "category", label: "Category", flex: 1, minWidth: 120, align: "left" },
    { key: "overtime", label: "Overtime", flex: 1, minWidth: 110, align: "left" },
    { key: "otWindow", label: "OT Window", flex: 1.5, minWidth: 180, align: "left" },
    { key: "status", label: "Status", flex: 1, minWidth: 120, align: "left" },
    { key: "action", label: "Action", flex: 1.2, minWidth: 160, align: "left" },
  ];

  const totalOvertimeSum = statsRows
    .reduce((acc, curr) => acc + (Number(curr.overtimeHrs) || 0), 0)
    .toFixed(2);
  const pendingCount = statsRows.filter((r) => !r.isVerified && !r.isRejected).length;
  const verifiedCount = statsRows.filter((r) => r.isVerified).length;
  const rejectedCount = statsRows.filter((r) => r.isRejected).length;

  const selectedEmployeeName =
    employeeOptions.find((e) => e.id === inputEmpId)?.name || "All Staff";

  return (
    <>
      {openVerifyModal && (
        <Modal open={openVerifyModal} onClose={handleCloseVerifyModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              bgcolor: "background.paper",
              borderRadius: "8px",
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600, color: "#2c3e50", mb: 2 }}
            >
              Confirm Verification for {selectedVerifyItem?.empName}
            </Typography>
            <div className="w-full">
              <label
                htmlFor="rejectionComment"
                className="block mb-2 text-sm font-medium text-gray-700"
              >
                Rejection Comment
              </label>

              <textarea
                id="rejectionComment"
                value={rejectionComment}
                onChange={(e) => setRejectionComment(e.target.value)}
                placeholder="Enter rejection comment"
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                    outline-none transition
                    focus:border-red-500 focus:ring-2 focus:ring-red-100
                    resize-none"
              />
            </div>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCloseVerifyModal}
                disabled={verifying}
                sx={{ borderColor: "#D1D5DB", color: "#374151" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleConfirmVerify("reject")}
                disabled={verifying}
                sx={{ bgcolor: "#db5126", "&:hover": { bgcolor: "#da3605" } }}
              >
                {verifying ? "Rejecting..." : "Reject"}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleConfirmVerify("verify")}
                disabled={verifying}
                sx={{ bgcolor: "#34495e", "&:hover": { bgcolor: "#2c3e50" } }}
              >
                {verifying ? "Verifying..." : "Verify"}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          p: 5,
          bgcolor: "#F9FAFB",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          my: 4,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              sx={{
                color: "#111827",
                fontSize: 28,
                fontFamily: FONT,
                fontWeight: 700,
              }}
            >
              Overtime Details
            </Typography>
            <Typography
              sx={{ color: "#6B7280", fontSize: 14, fontFamily: FONT, mt: 0.5 }}
            >
              Showing {totalCount} records across all employee shifts and
              logistics operations
            </Typography>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={handleExportCSV}
            sx={{
              px: 2.25,
              py: 1.5,
              bgcolor: "white",
              borderRadius: "8px",
              outline: "1px solid #D1D5DB",
              outlineOffset: "-1px",
              cursor: "pointer",
              "&:hover": { bgcolor: "#F9FAFB" },
            }}
          >
            <DownloadIcon sx={{ fontSize: 16, color: "#374151" }} />
            <Typography
              sx={{
                color: "#374151",
                fontSize: 14,
                fontFamily: FONT,
                fontWeight: 600,
              }}
            >
              Export CSV
            </Typography>
          </Stack>
        </Stack>

        {/* Filters */}
        <Stack
          direction="column"
          spacing={2}
          sx={{
            p: 2.5,
            bgcolor: "#F3F4F6",
            borderRadius: "16px",
            outline: "1px solid #E5E7EB",
            outlineOffset: "-1px",
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ width: "100%", flexWrap: "wrap" }}
          >
            <Box sx={{ flex: 1, minWidth: "180px" }}>
              <FilterPill
                label="Employee Name"
                value={selectedEmployeeName}
                options={employeeOptions}
                onChange={(e) => setInputEmpId(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: "180px" }}>
              <TextField
                fullWidth
                size="small"
                label="Booking ID"
                placeholder="Enter Booking ID"
                value={inputBookingId}
                onChange={(e) => setInputBookingId(e.target.value)}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                }}
              />
            </Box>
            <Box
              sx={{
                flex: 1.5,
                minWidth: "260px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <DaterangePicker
                selectionRange={selectionRange}
                setSelectionRange={setSelectionRange}
                style="0"
                renderInput={({ open, onClick }) => (
                  <TextField
                    fullWidth
                    size="small"
                    label="Date Range"
                    value={`${selectionRange.startDate.toLocaleDateString()} - ${selectionRange.endDate.toLocaleDateString()}`}
                    onClick={onClick}
                    InputProps={{ readOnly: true }}
                    sx={{
                      bgcolor: "white",
                      borderRadius: "8px",
                      cursor: "pointer",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        cursor: "pointer",
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={handleResetClick}
              sx={{
                borderColor: "#D43535",
                color: "#D43535",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: "#b52b2b", bgcolor: "#FFF8F8" },
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearchClick}
              sx={{
                bgcolor: "#34495e",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#2c3e50" },
              }}
            >
              Search
            </Button>
          </Stack>
        </Stack>

        {/* Stats */}
        <Stack direction="row" spacing={2.5}>
          <StatCard
            label="Total Overtime Hours"
            value={`${totalOvertimeSum} hrs`}
            badge="Active Shifts"
            badgeColor="#059669"
            badgeBg="#ECFDF5"
            barColor="#2376FF"
          />
          <StatCard
            label="Pending Approvals"
            value={`${pendingCount} records`}
            badge="Needs Action"
            badgeColor="#D97706"
            badgeBg="#FFF7ED"

            barColor="#D97706"
          />
          <StatCard
            label="Rejected"
            value={`${rejectedCount} records`}
            badge="Needs Action"
            badgeColor="#d90d06"
            badgeBg="#FFF7ED"

            barColor="#d91406"
          />
          <StatCard
            label="Verified Records"
            value={`${verifiedCount} records`}
            badge="Completed"
            badgeColor="#059669"
            badgeBg="#ECFDF5"

            barColor="#2376FF"
          />
        </Stack>

        {/* Table Container */}
        <Box
          sx={{
            bgcolor: "white",
            borderRadius: "24px",
            outline: "1px solid #E5E7EB",
            outlineOffset: "-1px",
            overflow: "hidden",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            {/* Table Header */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                px: 3,
                py: 2,
                bgcolor: "#F9FAFB",
                borderBottom: "1px solid #E5E7EB",
                minWidth: "1200px",
              }}
            >
              {columns.map((c) => (
                <Box
                  key={c.key}
                  sx={{
                    flex: c.flex,
                    minWidth: c.minWidth,
                    textAlign: c.align,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#4B5563",
                      fontSize: 13,
                      fontFamily: FONT,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {c.label}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Table Body */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography
                  sx={{ color: "#6B7280", fontFamily: FONT, fontSize: 14 }}
                >
                  No overtime records found.
                </Typography>
              </Box>
            ) : (
              rows.map((r, i) => {
                const isPositive = Number(r.overtimeHrs) >= 0;
                const isTripStart = r.overtimeEntity === "tripStart";
                return (
                  <Stack
                    key={r.id}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      px: 3,
                      py: 2,
                      minWidth: "1200px",
                      borderBottom:
                        i < rows.length - 1 ? "1px solid #F3F4F6" : "none",
                      "&:hover": { bgcolor: "#FAFAFA" },
                    }}
                  >
                    {/* Agent */}
                    <Box
                      sx={{
                        flex: columns[0].flex,
                        minWidth: columns[0].minWidth,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#111827",
                          fontSize: 14,
                          fontFamily: FONT,
                          fontWeight: 600,
                        }}
                      >
                        {r.empName}
                      </Typography>
                      <Stack direction="column" spacing={0.25} mt={0.25}>
                        <Typography
                          sx={{
                            color: "#6B7280",
                            fontSize: 12,
                            fontFamily: FONT,
                          }}
                        >
                          {r.empEmail}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography
                            onClick={() => {
                              const navigate = `/viewallbookings?bookingid=${r?.bookingCode}&id=${r?.bookingId}&display=true`;
                              window.open(navigate, "_blank");
                            }}
                            sx={{
                              color: "#031b3b",
                              fontSize: 12,
                              fontFamily: FONT,
                              fontWeight: 500,
                              cursor: "pointer",
                            }}
                          >
                            {r.bookingCode || r.bookingId
                              ? `Booking: ${r.bookingCode || r.bookingId}`
                              : "No Booking"}
                          </Typography>
                          {(r.bookingCode || r.bookingId) && (
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(
                                  String(r.bookingCode || r.bookingId),
                                );
                                toast.success("Booking ID copied!", {
                                  position: "top-center",
                                  style: {
                                    background: "white",
                                    color: "#111827",
                                    fontWeight: "550",
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                                    border: "1px solid #E5E7EB",
                                  },
                                });
                              }}
                              sx={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                color: "#6B7280",
                                "&:hover": { color: "#111827" },
                              }}
                              title="Copy Booking ID"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="9"
                                  y="9"
                                  width="13"
                                  height="13"
                                  rx="2"
                                  ry="2"
                                ></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    </Box>

                    {/* Shift Start */}
                    <Box
                      sx={{
                        flex: columns[1].flex,
                        minWidth: columns[1].minWidth,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#111827",
                          fontSize: 13.5,
                          fontFamily: FONT,
                        }}
                      >
                        {r.shiftStartTime || "N/A"}
                      </Typography>
                    </Box>

                    {/* Shift End */}
                    <Box
                      sx={{
                        flex: columns[2].flex,
                        minWidth: columns[2].minWidth,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#111827",
                          fontSize: 13.5,
                          fontFamily: FONT,
                        }}
                      >
                        {r.shiftEndTime || "N/A"}
                      </Typography>
                    </Box>

                    {/* Total Shift */}
                    <Box
                      sx={{
                        flex: columns[3].flex,
                        minWidth: columns[3].minWidth,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#111827",
                          fontSize: 13.5,
                          fontFamily: FONT,
                        }}
                      >
                        {Number(r.totalShiftHrs).toFixed(2)} hrs
                      </Typography>
                    </Box>

                    {/* Category */}
                    <Box
                      sx={{
                        flex: columns[4].flex,
                        minWidth: columns[4].minWidth,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 0.4,
                          bgcolor: isTripStart ? "#F3F4F6" : "#000000",
                          borderRadius: "6px",
                        }}
                      >
                        <Typography
                          sx={{
                            color: isTripStart ? "#4B5563" : "#FFFFFF",
                            fontSize: 12,
                            fontFamily: FONT,
                            fontWeight: 600,
                            textTransform: "capitalize",
                          }}
                        >
                          {isTripStart ? "Trip Start" : "Trip End"}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Overtime */}
                    <Box
                      sx={{
                        flex: columns[5].flex,
                        minWidth: columns[5].minWidth,
                      }}
                    >
                      <Typography
                        sx={{
                          color: isPositive ? "#059669" : "#DC2626",
                          fontSize: 13.5,
                          fontFamily: FONT,
                          fontWeight: 700,
                        }}
                      >
                        {isPositive
                          ? `+${Number(r.overtimeHrs).toFixed(2)}`
                          : Number(r.overtimeHrs).toFixed(2)}{" "}
                        hrs
                      </Typography>
                    </Box>

                    {/* OT Window */}
                    <Box
                      sx={{
                        flex: columns[6].flex,
                        minWidth: columns[6].minWidth,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#4B5563",
                          fontSize: 12.5,
                          fontFamily: FONT,
                        }}
                      >
                        {r.otStartDateTime && r.otEndDateTime
                          ? `${r.otStartDateTime} - ${r.otEndDateTime}`
                          : "N/A"}
                      </Typography>
                    </Box>

                    {/* Status */}
                    <Box
                      sx={{
                        flex: columns[7].flex,
                        minWidth: columns[7].minWidth,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <StatusBadge
                        verified={r.isVerified}
                        rejected={r.isRejected}
                      />
                    </Box>

                    {/* Action */}
                    <Box
                      sx={{
                        flex: columns[8].flex,
                        minWidth: columns[8].minWidth,
                      }}
                    >
                      {actionsToAccess?.includes("Approve Reject Overtime") ? (
                        <Box
                          onClick={() => handleOpenVerifyModal(r)}
                          sx={{ cursor: "pointer" }}
                        >
                          {r.isVerified ? (
                            <div>
                              <span className="text-xs font-medium text-gray-500">
                                Verified at{" "}
                                {r.verifiedAt
                                  ? formatDateAndTime(r.verifiedAt)
                                  : "N/A"}
                              </span>
                              <br />
                              <span className="text-xs font-medium text-gray-500">
                                Verified by {r?.verifiedBy}
                              </span>
                            </div>
                          ) : r.isRejected ? (
                            <div>
                              <span className="text-xs font-medium text-gray-500">
                                Rejection Comment {r?.rejectionComment}
                              </span>
                              <br />
                              <span className="text-xs font-medium text-gray-500">
                                Rejected at{" "}
                                {r.rejectedAt
                                  ? formatDateAndTime(r.rejectedAt)
                                  : "N/A"}
                              </span>
                              <br />
                              <span className="text-xs font-medium text-gray-500">
                                Rejected by {r?.rejectedBy}
                              </span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleOpenVerifyModal(r)}
                              className="px-3 py-1.5 bg-[#34495e] text-white text-xs font-semibold rounded hover:bg-[#2c3e50] transition-colors"
                            >
                              Action
                            </button>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ color: "#9CA3AF", fontSize: 12 }}>
                          Need Action to verify
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                );
              })
            )}
          </Box>

          {/* Table Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={() => {}}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            sx={{
              borderTop: "1px solid #E5E7EB",
              ".MuiTablePagination-toolbar": { minHeight: "52px" },
              ".MuiTablePagination-displayedRows": {
                fontFamily: FONT,
                fontSize: 13,
                color: "#4B5563",
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default Overtime;